import { Info } from "@phosphor-icons/react";
import { clsx } from "clsx";
import { NavLink, useLocation } from "react-router-dom";

import { NavItem } from "features/layout/components";

import RelativeLink from "common/components/RelativeLink";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";
import { getWhitelabelCustomization } from "common/helpers/utils";

import useSettingsNavItems from "../../hooks/useSettingsNavItems";

import AgencyIcon from "assets/icons/agency-icon.svg?react";
import UpgradeIcon from "assets/icons/agency-upgrade.svg?react";
import AgencyUpgradeImage from "assets/images/agency-upgrade-img.png";

export default function AgencyUpgrade() {
  const { pathname } = useLocation();
  const { role } = useSelectedWorkspaceContext();
  const { agencyPlan } = usePromotions();
  const { agencyNavItems } = useSettingsNavItems();

  const isVisible = role === "owner" && !getWhitelabelCustomization();
  const hasAgencyPlan = !!agencyPlan;
  const isActive =
    pathname.includes("/settings/agency") ||
    pathname.includes("/settings/whitelabel");

  if (!isVisible) {
    return null;
  }

  const agencyTitleComponent = (
    <span
      className={clsx([
        "flex flex-row gap-1 items-center font-semibold",
        hasAgencyPlan ? "text-button-xs" : "text-button-xxs",
      ])}
    >
      Agency plan
      <Tooltip>
        <TooltipTrigger asChild>
          <Info size={16} weight="fill" className="fill-blue-400" />
        </TooltipTrigger>

        <TooltipContent>
          Agency works at the profile-level and provides Outreach seats to all
          your workspaces.
        </TooltipContent>
      </Tooltip>
    </span>
  );

  const containerClassName = clsx(
    "flex flex-col gap-0.5 shadow-md overflow-hidden rounded-4xl mb-2 transition-[box-shadow,outline] hover:shadow-lg",
    isActive && "ring-1 ring-purple-600",
  );

  if (hasAgencyPlan) {
    return (
      <div className={containerClassName}>
        <div className="flex flex-col gap-1 p-1 bg-[url(/images/agency-upgrade-bg.webp)] bg-center bg-cover">
          <div className="flex p-2.5 flex-row gap-2">
            <AgencyIcon className="shadow-[inset_0_0_0.75px_0.75px_rgba(255,255,255,0.16),0_0_0_0.9px_rgba(243,244,246,0.50),0_10.5px_7.5px_0_rgba(224,229,255,0.70),0_10.5px_7.5px_0_rgba(85,116,255,0.10),0_5.25px_3.75px_0_rgba(119,85,255,0.10)]" />
            {agencyTitleComponent}
          </div>

          <div className="bg-white flex flex-col gap-1 rounded-3xl p-1">
            {agencyNavItems.map((navItem) => {
              if ("element" in navItem) {
                return navItem.element;
              }
              const { path, icon, label } = navItem;
              return (
                <NavLink to={path} key={path}>
                  {({ isActive: isRouteActive }) => (
                    <NavItem isExpanded isActive={isRouteActive} icon={icon}>
                      {label}
                    </NavItem>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <RelativeLink to="settings/agency" className={containerClassName}>
      <div className="flex overflow-hidden flex-col gap-4 px-3.5 py-4 rounded-4xl bg-[url(/images/agency-upgrade-bg.webp)] bg-center bg-cover">
        <img src={AgencyUpgradeImage} alt="" className="w-full h-full" />

        <div className="flex flex-row gap-2">
          <UpgradeIcon className="rounded-lg shadow-[0px_7px_5px_0px_#7755FF1A,0px_14px_10px_0px_#5574FF1A,0px_14px_10px_0px_#E0E5FFB2,0px_0px_0px_1.2px_#F3F4F680,inset_0px_0px_1px_1px_#FFFFFF29]" />

          <div className="flex flex-col">
            {agencyTitleComponent}

            <span className="text-xxs text-black-500">
              Scale across workspaces
            </span>
          </div>
        </div>
      </div>

      <span className="text-button-sm font-semibold text-purple-600 py-1 flex items-center justify-center">
        Upgrade now
      </span>
    </RelativeLink>
  );
}
